/**
 * Finanzas > Reportes: the period a report covers and how a figure reads
 * against the period before it. Days are Panama calendar days (`YYYY-MM-DD`),
 * the same calendar the P&L and the API use, so "hoy" at 10:30 p. m. is still
 * today even though UTC has moved on.
 */

export type ReportPreset = 'hoy' | '7dias' | 'mes' | 'mes-pasado' | 'anio';
export type ReportPeriod = { from: string; to: string };

export const REPORT_PRESETS: readonly { id: ReportPreset; label: string }[] = [
  { id: 'hoy', label: 'Hoy' },
  { id: '7dias', label: '7 días' },
  { id: 'mes', label: 'Este mes' },
  { id: 'mes-pasado', label: 'Mes pasado' },
  { id: 'anio', label: 'Este año' },
];

const BUSINESS_TIME_ZONE = 'America/Panama';
const DAY_KEY = new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIME_ZONE });
const SHORT_DAY = new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const MONTH_NAME = new Intl.DateTimeFormat('es-PA', { month: 'long', timeZone: 'UTC' });
/** A change smaller than this reads as "igual": nobody acts on half a percent. */
const FLAT_CHANGE_PERCENT = 0.5;

function dayKey(date: Date): string {
  return DAY_KEY.format(date);
}

/** A `YYYY-MM-DD` as a UTC-noon Date, so calendar math never crosses a day boundary. */
function parseDay(key: string): Date {
  return new Date(`${key}T12:00:00.000Z`);
}

function toKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(key: string, days: number): string {
  const date = parseDay(key);
  date.setUTCDate(date.getUTCDate() + days);
  return toKey(date);
}

export function periodFor(preset: ReportPreset, now: Date): ReportPeriod {
  const today = dayKey(now);
  const [year, month] = today.split('-');
  switch (preset) {
    case 'hoy':
      return { from: today, to: today };
    case '7dias':
      return { from: addDays(today, -6), to: today };
    case 'mes':
      return { from: `${year}-${month}-01`, to: today };
    case 'mes-pasado': {
      const lastOfPrevious = addDays(`${year}-${month}-01`, -1);
      return { from: `${lastOfPrevious.slice(0, 7)}-01`, to: lastOfPrevious };
    }
    case 'anio':
      return { from: `${year}-01-01`, to: today };
  }
}

/** Percent change against the previous period; null when the previous period had nothing. */
export function changeAgainst(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

export function formatChange(change: number | null): string | null {
  if (change === null) return null;
  if (Math.abs(change) < FLAT_CHANGE_PERCENT) return 'igual';
  const rounded = Math.round(Math.abs(change));
  return `${change > 0 ? '+' : '−'}${rounded} %`;
}

export function shareOf(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((part / total) * 100);
}

function isWholeMonth(period: ReportPeriod): boolean {
  if (!period.from.endsWith('-01') || period.from.slice(0, 7) !== period.to.slice(0, 7)) return false;
  return addDays(period.to, 1).endsWith('-01');
}

function shortDay(key: string): string {
  return SHORT_DAY.format(parseDay(key)).replace('.', '');
}

/** "24 sept 2026", "agosto 2026", "1 al 24 sept 2026", "28 ago al 3 sept 2026". */
export function periodLabel(period: ReportPeriod): string {
  const fromYear = period.from.slice(0, 4);
  const toYear = period.to.slice(0, 4);
  if (period.from === period.to) return `${shortDay(period.from)} ${fromYear}`;
  if (isWholeMonth(period)) return `${MONTH_NAME.format(parseDay(period.from))} ${fromYear}`;
  if (period.from.slice(0, 7) === period.to.slice(0, 7)) {
    return `${Number(period.from.slice(8))} al ${shortDay(period.to)} ${toYear}`;
  }
  const start = fromYear === toYear ? shortDay(period.from) : `${shortDay(period.from)} ${fromYear}`;
  return `${start} al ${shortDay(period.to)} ${toYear}`;
}

export type RevenueBucket = { key: string; label: string; revenueCents: number };

/** Up to two months reads by day, up to half a year by week, anything longer by month. */
const DAILY_MAX_DAYS = 62;
const WEEKLY_MAX_DAYS = 190;

function daysBetween(period: ReportPeriod): number {
  return Math.round((parseDay(period.to).getTime() - parseDay(period.from).getTime()) / 86_400_000) + 1;
}

function mondayOf(key: string): string {
  const weekday = parseDay(key).getUTCDay(); // 0 Sunday
  return addDays(key, -((weekday + 6) % 7));
}

/**
 * The revenue chart's columns: every day (or week, or month) of the period in
 * order, zero where nothing came in, so a quiet day reads as quiet instead of
 * disappearing from the axis.
 */
export function revenueBuckets(
  days: readonly { date: string; revenueCents: number }[],
  period: ReportPeriod,
): RevenueBucket[] {
  const span = daysBetween(period);
  const grain = span <= DAILY_MAX_DAYS ? 'day' : span <= WEEKLY_MAX_DAYS ? 'week' : 'month';
  const keyOf = (day: string) => (grain === 'day' ? day : grain === 'week' ? mondayOf(day) : day.slice(0, 7));
  const labelOf = (key: string) =>
    grain === 'day'
      ? shortDay(key)
      : grain === 'week'
        ? `Semana del ${shortDay(key)}`
        : MONTH_NAME.format(parseDay(`${key}-01`));

  const totals = new Map<string, number>();
  for (let day = period.from; day <= period.to; day = addDays(day, 1)) {
    const key = keyOf(day);
    if (!totals.has(key)) totals.set(key, 0);
  }
  for (const entry of days) {
    if (entry.date < period.from || entry.date > period.to) continue;
    const key = keyOf(entry.date);
    totals.set(key, (totals.get(key) ?? 0) + entry.revenueCents);
  }
  return [...totals.entries()].map(([key, revenueCents]) => ({ key, label: labelOf(key), revenueCents }));
}
